import Skeleton from '@/global/components/Skeleton'
import Grid from '@/global/components/ui/Grid'
import { FormField, FormFieldAutocomplete } from 'global/components/Form'
import type { IMapHandlerProps } from 'global/components/Map'
import { MapPinPlusIcon } from 'lucide-react'
import { lazy, Suspense, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from 'use-places-autocomplete'

const MapComponent = lazy(() =>
    import('global/components/Map').then(m => ({ default: m.MapComponent })),
)

type Position = google.maps.LatLngLiteral

type FormProps = {
    latitude: number
    longitude: number
    cercaEletronica: number
    endereco: string
}

export const LocalForm = () => {
    const mapRef = useRef<IMapHandlerProps>(null)
    const { setValue, watch } = useFormContext<FormProps>()

    const { cercaEletronica, latitude, longitude } = watch()

    const {
        init,
        setValue: setBusca,
        suggestions: { data: enderecos },
    } = usePlacesAutocomplete({ debounce: 300, initOnMount: false })
    const [busca, setBuscaLocal] = useState('')

    const handleSetCircleMarker = (ev: any) => {
        const { value } = ev.target
        mapRef.current?.setMarkerCircle(+value)
    }

    const handleGetMapPosition = async ({ lat, lng }: Position) => {
        setValue('latitude', lat)
        setValue('longitude', lng)

        const address = await getGeocode({ location: { lat, lng } })
        const first = address.at(0)

        if (first) {
            setValue('endereco', first.formatted_address, {
                shouldDirty: true,
                shouldTouch: true,
            })
        }
    }

    const handleSetOffice = ({ lat, lng }: Position) => {
        mapRef.current?.setMapPosition({ lat, lng })
        mapRef.current?.setMapZoom(16)

        setValue('latitude', lat)
        setValue('longitude', lng)
    }

    const handleSelecionarEndereco = async (endereco: string | null) => {
        if (!endereco) return

        const position = await getPosicaoDoEndereco(endereco)
        if (position) handleSetOffice(position)
    }

    return (
        <Grid container>
            <Grid item xs={8}>
                <FormFieldAutocomplete
                    name="endereco"
                    label="Endereço"
                    defaultValue=""
                    options={enderecos.map(local => local.description)}
                    paraCampo={endereco => endereco || null}
                    paraFormulario={endereco => endereco ?? ''}
                    inputValue={busca}
                    onInputChange={texto => {
                        setBuscaLocal(texto)
                        if (texto.trim().length > 2) setBusca(texto)
                    }}
                    onChange={handleSelecionarEndereco}
                    renderOption={endereco => (
                        <span className="flex items-center gap-1">
                            <MapPinPlusIcon className="shrink-0 text-destructive" />
                            {endereco}
                        </span>
                    )}
                    noOptionsText="Digite ao menos 3 letras do endereço"
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormField
                    name="cercaEletronica"
                    label="Cerca eletrônica"
                    onChange={handleSetCircleMarker}
                    required
                    endAdornment={
                        <span className="text-sm text-muted-foreground">m</span>
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Suspense fallback={<Skeleton className="h-80" />}>
                    <MapComponent
                        height="20rem"
                        getMapPosition={handleGetMapPosition}
                        onScriptLoaded={init}
                        ref={mapRef}
                        lat={+latitude}
                        lng={+longitude}
                        radius={+cercaEletronica}
                    />
                </Suspense>
            </Grid>
        </Grid>
    )
}

async function getPosicaoDoEndereco(address: string) {
    try {
        const results = await getGeocode({ address })
        return await getLatLng(results[0])
    } catch (_error) {
        //
    }
}
